import { BRICK_COLOR_HEX } from '../engine/robloxValues'
import { playSample } from './audio'
import { GOAL_SUCCESS_SFX } from './missionSfx'
import { findStateByName } from './sharedHelpers'
import { BehaviorHandler, HudStyle, MissionBehaviors } from './types'

/**
 * RJS01（3 れんぞくクイズ）のミッション環境ビヘイビア。
 *
 * テンプレート rbxl（src/missions/templates/RJS01.rbxl）のギミックを移植する:
 * - ServerScriptService/GameManager: 3 つのクイズを順番に毎秒監視する。
 *   ボール（①1つめのボール → ②2つめのボール → ⑥3つめのボール）が
 *   BrickColor.White() になったら Winning Spirit を鳴らし、対応する壁
 *   （Wall1 → Wall2 → Wall3）を destroy して次のクイズへ進む。
 * - StarterGui/LocalScript: ReplicatedStorage.Info の初期値
 *   「ボールのXざひょうを 50よりおおきくしろ」を表示し、クイズクリアの
 *   たびに「クリア！」→ 3 秒後に次の指示へ切り替える。最終クイズは
 *   「クリアおめでとう！」を出しっぱなしにする。
 *
 * プレビューでの近似・意図的な差異:
 * - 実機の GUI 遷移（クリア！→ 3 秒 → 次の指示）はクライアント側で
 *   GameManager の監視と並行に走るが、プレビューは 1 本のループで逐次
 *   実行する（次クイズの監視開始が最大 3 秒遅れるだけで結果は同じ。
 *   3 秒以内に次が解かれたときの実機の表示競合は再現しない）。
 * - クイズクリア音（Winning Spirit）は実音源（GOAL_SUCCESS_SFX）で鳴らす。
 * - ②2つめのボール/Ball2Script（Anchored 化の瞬間に速度を恒久的にゼロ化）は、
 *   objectBodyRegistry がこの Script パターンを検出し、再アンカー時に速度を
 *   退避しない（次のアンカー解除で必ず静止状態から再開する）ため移植不要。
 *
 * 未対応:
 * - Tool 'ClassicSlingshot' / 'ClassicSuperball'（パチンコ・スーパーボール）:
 *   装備して使う道具で、プレビューに装備（Tool）システムが無いため移植
 *   できない。原作にも ClickDetector 等の代替トリガは無い。
 * - BGM はシーン JSON の bgm 機構の担当（ビヘイビア対象外）。実機の 'Funny Days'
 *   はプロダクト指定の Sunrise（bgm-overrides.json）へ差し替え済み。
 */

/** クイズ監視の間隔（Lua: GameManager の wait(1)）。 */
const QUIZ_POLL_INTERVAL_SECONDS = 1

/** 「クリア！」を出してから次の指示へ切り替えるまでの秒数（Lua: wait(3)）。 */
const CLEAR_MESSAGE_SECONDS = 3

/**
 * プレイ開始時の指示（ReplicatedStorage.Info の初期値の実測）。
 * GameManager のクイズ 1 の条件（ボールを白にする）とは食い違うが、
 * 実機がこの文言を表示するため忠実に再現する。
 */
const INITIAL_INFO_TEXT = 'ボールのXざひょうを　50よりおおきくしろ'

/**
 * クイズ判定の白（Lua: BrickColor.White()）。学習者の「いろをかえる」
 * ブロックが設定する White と同じ 16 進値（robloxValues と共有）。
 */
const WHITE_HEX = BRICK_COLOR_HEX.White.toUpperCase()

/** 指示 GUI の実機デザイン（ScreenGui/TextLabel の実測値）。 */
const INFO_HUD_STYLE: HudStyle = {
  background: '#FFFFFF',
  color: '#000000',
  border: '#00AA00',
  borderWidth: 5,
  left: 10,
}

/** 1 クイズぶんの定義（監視するボール・壊す壁・クリア後の表示）。 */
interface QuizStep {
  /** 白を監視するボールのオブジェクト名。 */
  readonly ballName: string
  /** クリアで destroy する壁のオブジェクト名。 */
  readonly wallName: string
  /** クリア直後に表示する文言。 */
  readonly clearText: string
  /**
   * クリア表示の 3 秒後に出す次の指示。undefined は最終クイズ
   * （clearText を出しっぱなしにする）。
   */
  readonly nextText?: string
}

/** クイズの進行表（Lua: GameManager の quiz1〜quiz3 + LocalScript の文言）。 */
const QUIZ_STEPS: ReadonlyArray<QuizStep> = [
  {
    ballName: '①1つめのボール',
    wallName: 'Wall1',
    clearText: 'クリア！',
    nextText: 'Blueをわたって　ボールをもっていけ',
  },
  {
    ballName: '②2つめのボール',
    wallName: 'Wall2',
    clearText: 'クリア！',
    nextText: 'わたし　をさわって　クリックせよ',
  },
  {
    ballName: '⑥3つめのボール',
    wallName: 'Wall3',
    clearText: 'クリアおめでとう！',
  },
]

/** ボールの現在色が白（BrickColor.White()）か。大文字小文字の揺れを吸収する。 */
const isBrickWhite = (color: string): boolean =>
  color.toUpperCase() === WHITE_HEX

export const createRjs01Behaviors = (): MissionBehaviors => {
  /**
   * クイズ進行ループ（GameManager + StarterGui/LocalScript の統合）。
   * Lua と同じく「wait(1) → 判定」の順で毎秒監視し、クリアしたら次の
   * クイズへ進む（前のクイズを飛ばして後のクイズだけ解いても進まない）。
   */
  const runQuizzes: BehaviorHandler = async (ctx) => {
    const { world } = ctx
    // Lua: LocalScript 末尾の textLabel.Text = info.Value（初期指示の表示）。
    ctx.setHudText?.(INITIAL_INFO_TEXT, INFO_HUD_STYLE)
    for (const step of QUIZ_STEPS) {
      // Lua: while quizN == false do wait(1); 判定 end
      let cleared = false
      while (!cleared) {
        await ctx.wait(QUIZ_POLL_INTERVAL_SECONDS)
        if (ctx.signal.aborted) {
          return
        }
        const ball = findStateByName(world, step.ballName)
        if (!ball) {
          // Lua はボールを直接 index しているため、消えていれば GameManager
          // ごとエラー停止する。以降のクイズが進まない結果を揃える。
          return
        }
        cleared = isBrickWhite(ball.color)
      }
      // Lua: Winning Spirit:Play() → 壁 Destroy() → ClearEvent 発火。
      playSample(GOAL_SUCCESS_SFX.url, GOAL_SUCCESS_SFX.volume, ctx.signal)
      const wall = findStateByName(world, step.wallName)
      if (wall) {
        world.destroy(wall.id)
      }
      ctx.setHudText?.(step.clearText, INFO_HUD_STYLE)
      if (step.nextText !== undefined) {
        await ctx.wait(CLEAR_MESSAGE_SECONDS)
        if (ctx.signal.aborted) {
          return
        }
        ctx.setHudText?.(step.nextText, INFO_HUD_STYLE)
      }
    }
  }

  return { onPlay: [runQuizzes] }
}
